import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { InputBaseModel } from '../../models';
import { InputRenderer } from '../../components';
import { NodeProps } from '../../componentTypes';
import { LayoutRenderer } from '../../layout_renderers';
import { InputIds, NodeIds } from '../../misc';
import { Utils } from '../../../custom_shared/misc';
import { useNodeForm } from '../../hooks';
import { GetComponentRowsFunction } from '../../componentTypes';
import { ColumnSpan } from '../../types';
import { InputComponentRegistry } from '../../node_inputs';
import { AdditionalClassificationInput } from '../../node_inputs/SharedInputs';

const InputComponents = InputComponentRegistry[NodeIds.Classifications];
const AdditionalClassificationSourceIdPrefix = 'additionalClassification_';

const InputColumnSpans: Record<string, ColumnSpan> = {
    [InputIds.Classifications.MainClass]: 24,
    [InputIds.Classifications.Class]: 24,
    [InputIds.Classifications.SubClass]: 24
};

const getInputComponent = (input: InputBaseModel) => {
    const component = InputComponents[input.id as keyof typeof InputComponents];
    if (component) {
        return component;
    }

    return input.sourceId?.startsWith(AdditionalClassificationSourceIdPrefix)
        ? AdditionalClassificationInput
        : undefined;
};

const ClassificationsNodeRenderer: React.FC<NodeProps> = ({ node }: NodeProps) => {
    const { form, formKey, onValuesChange } = useNodeForm(node);

    const getComponentRows: GetComponentRowsFunction = React.useCallback(
        (inputs: InputBaseModel[]) =>
            Utils.groupArray(inputs, 1).map(inputGroup =>
                inputGroup.map(input => ({
                    component: <InputRenderer key={input.id} input={input} component={getInputComponent(input)} />,
                    columnSpan: InputColumnSpans[input.id] ?? 24
                }))
            ),
        []
    );

    return (
        <LayoutRenderer
            node={node}
            form={form}
            formKey={formKey}
            onValuesChange={onValuesChange}
            getComponentRows={getComponentRows}
        />
    );
};

export default observer(ClassificationsNodeRenderer);
